import React, { useState, useEffect } from 'react';
import { Hash, ClipboardList } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    AIButton, AIUndoButton, PollResults,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';

const DEFAULT_SETTINGS = { correctNumber: undefined, pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, description: false, descText: '' };

const DUMMY_DATA = {
    question: 'How many cups of chai are consumed in India every single day? (in millions)',
    settings: { correctNumber: 800, pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, description: true, descText: "Enter your best guess! The answer will be revealed after everyone has submitted. Closest guess wins 🏆" },
};

const GuessNumber = ({ event, activity, isLaunched, onLaunch, onStop, onNext, onPrev, hasNext, hasPrev, onSettingsUpdated }) => {
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);

    const { content, isLoading, isSaving, saveStatus, saveContent } = useActivityContent(event?.id, activity?.id);

    useEffect(() => {
        if (!content) return;
        setQuestion(content.question || '');
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const buildContent = () => ({ question, settings });
    const validate = () => !question.trim() ? 'Please enter a question.' : null;
    const updateSetting = (key, val) => setSettings(s => ({ ...s, [key]: val }));

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text: question }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousQuestion(question); setQuestion(data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImproving(false); }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_PATH}/ai/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'improve', text }) });
            if (res.ok) { const data = await res.json(); if (data.suggestion) { setPreviousDescText(settings.descText); updateSetting('descText', data.suggestion); } } else { alert('Failed to improve with AI. Please try again.'); }
        } catch (err) { console.error(err); alert('Failed to improve with AI. Please try again.'); } finally { setIsImprovingDesc(false); }
    };

    const handleUndoQuestion = () => { if (previousQuestion !== null) { setQuestion(previousQuestion); setPreviousQuestion(null); } };
    const handleUndoDesc = () => { if (previousDescText !== null) { updateSetting('descText', previousDescText); setPreviousDescText(null); } };

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience step="activity" name="Preview" event={event} activity={{ ...activity, content: buildContent() }} isPreview />
    );

    const left = (
        <>
            <div className={styles.questionCard}>
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && <AIUndoButton onClick={handleUndoQuestion} label="Undo" />}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input className={styles.questionInput} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. How many jelly beans are in the jar?" autoFocus onBlur={() => setEditingQuestion(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingQuestion(false); } }} />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingQuestion(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingQuestion(true); } }}>
                                {question || 'e.g. How many jelly beans are in the jar?'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && <AIUndoButton onClick={handleUndoDesc} label="Undo" />}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input className={styles.descInput} value={settings.descText || ''} onChange={(e) => updateSetting('descText', e.target.value)} placeholder="Type description here..." autoFocus onBlur={() => setEditingDesc(false)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setEditingDesc(false); } }} />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`} role="button" tabIndex={0} onClick={() => setEditingDesc(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingDesc(true); } }}>
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-3)' }}>Correct Answer <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(reveal after voting)</span></p>
                        <input
                            type="number"
                            value={settings.correctNumber ?? ''}
                            onChange={e => updateSetting('correctNumber', e.target.value === '' ? undefined : Number(e.target.value))}
                            placeholder="Hidden until you reveal it"
                            className={styles.optionInput}
                            style={{ maxWidth: 200 }}
                        />
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>Participants will see who guessed closest after you reveal.</p>
                    </div>
                </div>
            </div>
        </>
    );

    const right = (
        <SettingsGroup title="Settings">
            <SettingsRow label="Poll description" description="Add description visible to participants">
                <Toggle checked={settings.description} onChange={(e) => updateSetting('description', e.target.checked)} />
            </SettingsRow>
            <SettingsRow label="Allow participants to update response" description="Let participants change their guess after submitting">
                <Toggle checked={settings.allowUpdateResponse} onChange={(e) => updateSetting('allowUpdateResponse', e.target.checked)} />
            </SettingsRow>
            <PollResults value={settings.pollResults} onChange={v => updateSetting('pollResults', v)} />
        </SettingsGroup>
    );

    return (
        <ActivityLayout
            icon={<Hash size={20} />} color="var(--cat-orange)"
            title="Guess the Number" eventName={event?.name}
            left={left} right={right} participantPreview={participantPreview}
            onSave={() => saveContent(buildContent())}
            onLoadExample={loadDummy}
            isSaving={isSaving} saveStatus={saveStatus} isLoading={isLoading}
            validate={validate} event={event} activity={activity} isLaunched={isLaunched}
            onLaunch={onLaunch} onStop={onStop} onNext={onNext} onPrev={onPrev}
            hasNext={hasNext} hasPrev={hasPrev} onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default GuessNumber;
